import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import fleetAnalyticsService from "@/services/mrfiktiv/services/fleet/analyticsFleetService";
import {
  MrfiktivBaseChartItemListViewmodelGen,
  MrfiktivBaseChartItemViewmodelGen,
  MrfiktivBaseTrendAnalysisViewmodelGen,
  MrfiktivInvoiceChartItemListViewModelGen,
  MrfiktivInvoiceTypeItemListViewModelGen,
  MrfiktivInvoiceTypeItemViewModelGen,
  MrfiktivReportWeeklyAnalysisPerPartnerItemGen,
  MrfiktivReportWeeklyAnalysisPerPartnerViewmodelGen,
  MrfiktivStatusDistributionAnalyticsItemGen,
  MrfiktivStatusDistributionAnalyticsViewModelGen,
  MrfiktivStatusValueDistributionAnalyticsItemGen,
  MrfiktivStatusValueDistributionAnalyticsViewModelGen,
  MrfiktivTotalDeductibleItemListViewModelGen,
  MrfiktivTotalDeductibleItemViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { costCenterFilters } from "@/lib/utility/cost-center-filter-helper";
@Module({
  dynamic: true,
  namespaced: true,
  name: "analyticsFleetStore",
  store
})
export class AnalyticsFleetStore extends VuexModule {
  filters: IPageFilterElement[] = [];
  private _dateRange: string[] = [
    new Date().toISOString(),
    new Date(new Date().setDate(new Date().getDate() - 365)).toISOString()
  ];

  get dateRange(): string[] {
    return this._dateRange;
  }

  get from(): string {
    const dates: number[] = [];
    for (const date of this.dateRange) {
      dates.push(new Date(date).getTime());
    }
    const tempo = new Date(new Date(Math.min.apply(null, dates)).setUTCHours(0, 0, 0, 0)).toISOString();
    return tempo;
  }

  get to(): string {
    const dates: number[] = [];
    for (const date of this.dateRange) {
      dates.push(new Date(date).getTime());
    }
    const tempo = new Date(new Date(Math.max.apply(null, dates)).setUTCHours(20, 59, 59, 999)).toISOString();
    return tempo;
  }

  trendAnalysisData: MrfiktivBaseTrendAnalysisViewmodelGen = {
    data: {
      sum: 0,
      dayTrend: [],
      sevenDayTend: [],
      thirtyDayTrend: []
    }
  };

  @Mutation
  _mutateDateRange(dateRange: string[]) {
    this._dateRange = dateRange;
  }

  @Action
  setDateRange(dateRange: string[]) {
    this.context.commit("_mutateDateRange", dateRange);
  }

  @Action
  setCostCenterFiltersFromIds({ ids, keyType }: { ids: string[]; keyType: "costCenter" | "report.costCenter" }) {
    const filters: IPageFilterElement[] = [];

    if (ids?.length > 0) {
      filters.push(
        new PageFilterElement({
          key: keyType,
          value: ids[0],
          operation: PageFilterOperationEnum.EQUAL
        })
      );
    }

    this.context.commit("_mutateCostCenterFilters", filters);
  }

  @Mutation
  _mutateCostCenterFilters(filters: IPageFilterElement[]) {
    const emptyCostCenter = this.filters.filter(f => f.key !== "costCenter" && f.key !== "report.costCenter");
    this.filters = [...emptyCostCenter, ...filters];
  }

  // Damage type subtotal

  private _damageTypeSubtotalData: MrfiktivInvoiceTypeItemViewModelGen[] = [];

  get damageTypeSubtotalOutboundData(): MrfiktivInvoiceTypeItemViewModelGen[] {
    return this._damageTypeSubtotalData;
  }

  @Action
  async getDamageTypeSubtotalData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters(
      { costCenterIds: data.costCenterIds, filters: data.filters },
      "report.costCenter"
    );
    const result: MrfiktivInvoiceTypeItemListViewModelGen = await fleetAnalyticsService.getTotalSubtotalPerDamageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateDamageTypeSubtotalData", result.data);
  }

  @Mutation
  _mutateDamageTypeSubtotalData(data: MrfiktivInvoiceTypeItemViewModelGen[]) {
    this._damageTypeSubtotalData = data;
  }

  // Coverage subtotal & deductible cost

  private _coverageSubtotalDeductibleData: MrfiktivTotalDeductibleItemViewModelGen[] = [];

  get coverageSubtotalDeductibleData(): MrfiktivTotalDeductibleItemViewModelGen[] {
    return this._coverageSubtotalDeductibleData;
  }

  @Action
  async getCoverageSubtotalDeductible(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters(
      { costCenterIds: data.costCenterIds, filters: data.filters },
      "report.costCenter"
    );
    const result: MrfiktivTotalDeductibleItemListViewModelGen = await fleetAnalyticsService.getCoverageSubtotalDeductible(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );
    this.context.commit("_mutateCoverageSubtotalDeductibleData", result.data);
  }

  @Mutation
  _mutateCoverageSubtotalDeductibleData(data: MrfiktivTotalDeductibleItemViewModelGen[]) {
    this._coverageSubtotalDeductibleData = data;
  }

  // Coverage type subtotal

  private _coverageTypeSubtotalData: MrfiktivInvoiceTypeItemViewModelGen[] = [];

  get coverageTypeSubtotalOutboundData(): MrfiktivInvoiceTypeItemViewModelGen[] {
    return this._coverageTypeSubtotalData;
  }

  @Action
  async getCoverageTypeSubtotalData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters(
      { costCenterIds: data.costCenterIds, filters: data.filters },
      "report.costCenter"
    );
    const result: MrfiktivInvoiceTypeItemListViewModelGen = await fleetAnalyticsService.getTotalSubtotalPerCoverageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateCoverageTypeSubtotalData", result.data);
  }

  @Mutation
  _mutateCoverageTypeSubtotalData(data: MrfiktivInvoiceTypeItemViewModelGen[]) {
    this._coverageTypeSubtotalData = data;
  }

  // Trip type count

  private _tripTypeData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get tripTypeData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._tripTypeData;
  }

  @Action
  async getTripTypeData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getCountPerTripType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateTripTypeData", result.data);
  }

  @Mutation
  _mutateTripTypeData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._tripTypeData = data;
  }

  // Damage type count

  private _damageTypeData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get damageTypeData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._damageTypeData;
  }

  @Action
  async getDamageTypeData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getCountPerDamageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateDamageTypeData", result.data);
  }

  @Mutation
  _mutateDamageTypeData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._damageTypeData = data;
  }

  // Coverage type count

  private _coverageTypeData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get coverageTypeData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._coverageTypeData;
  }

  @Action
  async getCoverageTypeData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) {
      return;
    }

    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getCountPerCoverageType(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateCoverageTypeData", result.data);
  }

  @Mutation
  _mutateCoverageTypeData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._coverageTypeData = data;
  }

  // Progress Status distribution

  private _progressStatusDistributionData: MrfiktivStatusDistributionAnalyticsItemGen[] = [];

  get progressStatusDistributionData(): MrfiktivStatusDistributionAnalyticsItemGen[] {
    return this._progressStatusDistributionData;
  }

  @Action
  async getProgressStatusDistribution(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivStatusDistributionAnalyticsViewModelGen = await fleetAnalyticsService.getProgressStatusDistribution(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateProgressStatusDistributionData", result.data);
  }

  @Mutation
  _mutateProgressStatusDistributionData(data: MrfiktivStatusDistributionAnalyticsItemGen[]) {
    this._progressStatusDistributionData = data;
  }

  // Status value distribution

  private _statusValueDistributionData: MrfiktivStatusValueDistributionAnalyticsItemGen[] = [];

  get statusValueDistributionData(): MrfiktivStatusValueDistributionAnalyticsItemGen[] {
    return this._statusValueDistributionData;
  }

  @Action
  async getStatusValueDistribution(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivStatusValueDistributionAnalyticsViewModelGen = await fleetAnalyticsService.getStatusValueDistribution(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateStatusValueDistributionData", result.data);
  }

  @Mutation
  _mutateStatusValueDistributionData(data: MrfiktivStatusValueDistributionAnalyticsItemGen[]) {
    this._statusValueDistributionData = data;
  }

  // Weekday distribution

  private _weekDayDistributionData: MrfiktivReportWeeklyAnalysisPerPartnerItemGen[] = [];

  get weekdayDistributionData(): MrfiktivReportWeeklyAnalysisPerPartnerItemGen[] {
    return this._weekDayDistributionData;
  }

  @Action
  async getWeekdayAnalysis(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivReportWeeklyAnalysisPerPartnerViewmodelGen = await fleetAnalyticsService.getWeekdayAnalysis(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateWeekDayData", result.data);
  }

  @Mutation
  _mutateWeekDayData(data: MrfiktivReportWeeklyAnalysisPerPartnerItemGen[]) {
    this._weekDayDistributionData = data;
  }

  // Workshop company distribution

  private _workshopCompanyDistributionData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get workshopCompanyDistributionData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._workshopCompanyDistributionData;
  }

  @Action
  async getWorkshopCompanyDistribution(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getWorkshopCompanyDistribution(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateWorkshopCompanyDistributionData", result.data);
  }

  @Mutation
  _mutateWorkshopCompanyDistributionData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._workshopCompanyDistributionData = data;
  }

  // Damage type average

  private _damageTypeAverageData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get damageTypeAverageData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._damageTypeAverageData;
  }

  @Action
  async getDamageTypeAverageData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getDamageTypeAverage(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateDamageTypeAverageData", result.data);
  }

  @Mutation
  _mutateDamageTypeAverageData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._damageTypeAverageData = data;
  }

  // Coverage type average

  private _coverageTypeAverageData: MrfiktivBaseChartItemViewmodelGen[] = [];

  get coverageTypeAverageData(): MrfiktivBaseChartItemViewmodelGen[] {
    return this._coverageTypeAverageData;
  }

  @Action
  async getCoverageTypeAverageData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivBaseChartItemListViewmodelGen = await fleetAnalyticsService.getCoverageTypeAverage(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateCoverageTypeAverageData", result.data);
  }

  @Mutation
  _mutateCoverageTypeAverageData(data: MrfiktivBaseChartItemViewmodelGen[]) {
    this._coverageTypeAverageData = data;
  }

  // Top creditor

  private _topCreditorData: MrfiktivInvoiceChartItemListViewModelGen[] = [];

  get topCreditorData(): MrfiktivInvoiceChartItemListViewModelGen[] {
    return this._topCreditorData;
  }

  @Action
  async getTopCreditorData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters(
      { costCenterIds: data.costCenterIds, filters: data.filters },
      "report.costCenter"
    );
    const result: MrfiktivInvoiceChartItemListViewModelGen = await fleetAnalyticsService.getTotalSubtotalBySeller(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateTopCreditorData", result.data);
  }

  @Mutation
  _mutateTopCreditorData(data: MrfiktivInvoiceChartItemListViewModelGen[]) {
    this._topCreditorData = data;
  }

  // Trend analysis

  @Action
  async getTrendAnalysisDataPerPartnerFleetId(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters({ costCenterIds: data.costCenterIds, filters: data.filters }, "costCenter");
    const result: MrfiktivBaseTrendAnalysisViewmodelGen = await fleetAnalyticsService.getTrendAnalysisDataPerPartnerFleetId(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );
    const trimmedData: MrfiktivBaseTrendAnalysisViewmodelGen = {
      data: {
        sum: result.data.sum,
        dayTrend: result.data.dayTrend.slice(-30),
        sevenDayTend: result.data.sevenDayTend.slice(-30),
        thirtyDayTrend: result.data.thirtyDayTrend.slice(-30)
      }
    };
    this.context.commit("_mutateTrendAnalysisData", trimmedData);
  }

  @Mutation
  _mutateTrendAnalysisData(data: MrfiktivBaseTrendAnalysisViewmodelGen) {
    this.trendAnalysisData = data;
  }

  // Top vehicle

  private _topVehicleData: MrfiktivInvoiceChartItemListViewModelGen[] = [];

  get topVehicleData(): MrfiktivInvoiceChartItemListViewModelGen[] {
    return this._topVehicleData;
  }

  @Action
  async getTopVehicleData(data: {
    partnerId: string;
    fleetId?: string;
    ids?: string[];
    costCenterIds?: string[];
    filters?: IPageFilterElement[];
  }) {
    if (!data.fleetId) return;
    const ids = data.ids ?? [data.fleetId];
    const finalFilters = costCenterFilters(
      { costCenterIds: data.costCenterIds, filters: data.filters },
      "report.costCenter"
    );
    const result: MrfiktivInvoiceChartItemListViewModelGen = await fleetAnalyticsService.getTotalSubtotalByVehicle(
      data.partnerId,
      data.fleetId,
      this.from,
      this.to,
      ids,
      finalFilters
    );

    this.context.commit("_mutateTopVehicleData", result.data);
  }

  @Mutation
  _mutateTopVehicleData(data: MrfiktivInvoiceChartItemListViewModelGen[]) {
    this._topVehicleData = data;
  }
}

export const AnalyticsFleetModule = getModule(AnalyticsFleetStore);
